import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText, CustomTextStyles } from '@/components/CustomText';
import { Link } from 'expo-router';
import Footer from '@/components/Footer';
import * as AppConsts from '@/utilities/AppConsts';
import * as AppConstants from '@/utilities/AppConstants';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'expo-router';
import { Image } from 'expo-image';
import { Button, TextInput } from 'react95-native-rabbl';
import { AppContext } from '@/components/AppContext';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import { setLoggedIn } from '@/state/user';
import { updateVisuals } from '@/state/uploads';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
  },
  flexRowGapVerticalCenter: {
    flexDirection: 'row',
    gap: 10,
    alignItems: "center",
  },
  editImage: {
    width: 32,
    height: 32,
  },
  passwordInput: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
});

export default function DeleteAccount() {
  const router = useRouter();
  const [password, setPassword] = useState("");
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const dispatch = useDispatch();
  const isMainLogicalHost = useSelector((state: RootState) => state.user.isMainLogicalHost);
  const loggedIn = useSelector((state: RootState) => state.user.loggedIn);
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const communityMemberships = useSelector((state: RootState) => state.user.communityMemberships);

  const onSubmit = useCallback(async() => {
    try {
      showSpinner();

      const response = await AppUtilities.fetchAuthorized(
        "DeleteAccount.onSubmit",
        "/rabbl/api/deleteaccount",
        "POST",
        {
          password: password,
        }
      );

      await AppUtils.logout(dispatch, showSpinner, hideSpinner);
      router.dismissAll();
      router.replace("/loggedout");

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(), true);
      AppUtilities.handleException(e, "DeleteAccount.onSubmit");
    } finally {
      hideSpinner();
    }
  },
  [
    password,
  ]);

  return (
    <BasicScrollView center={false}>
      <Meta title={"Delete account"} />
      {
        communityMemberships.length > 0 ?
            isMainLogicalHost ?
                <>
                  <AppText>Instead of deleting your account, you can hide your profile in each Rabbl you're in.</AppText>
                  <AppText>If you want to completely delete your account and all pictures, matches, and user information, then enter your password and select Delete Account below. This is irreversible and applies to all Rabbls you've joined.</AppText>
                </>
              :
                <>
                  <AppText>Instead of deleting your account, you can hide your profile under <Link href="/settings" style={CustomTextStyles.link}>settings</Link>.</AppText>
                  <AppText>If you want to completely delete your account and all pictures, matches, and user information, then enter your password and select Delete Account below. This is irreversible and applies to all Rabbls you've joined.</AppText>
                </>
          :
            <>
              <AppText>If you want to completely delete your account, then enter your password and select Delete Account below. This is irreversible and applies to all your data.</AppText>
            </>
      }
      <AppText>Re-enter your password:</AppText>
      <View style={AppConstants.STYLES.flexRow}>
        <TextInput
          value={password}
          onChangeText={setPassword}
          placeholder="Password"
          returnKeyType="next"
          autoComplete="off"
          textContentType="password"
          secureTextEntry={true}
          onSubmitEditing={onSubmit}
          style={styles.passwordInput}
        />
      </View>
      <View style={AppConstants.STYLES.flexRow}>
        <Button disabled={password.length == 0} onPress={onSubmit}>Delete account</Button>
      </View>
      <View style={AppConstants.STYLES.flexRow}>
        <Link href={AppUtils.getHomeLink(loggedIn, isMainLogicalHost, communityMembership)} asChild className={"nontext"}><Button>Nevermind</Button></Link>
      </View>
      <Footer />
    </BasicScrollView>
  );
}
